// Showcase content for the investor-facing landing. Numbers anchored in
// `showcase_metrics.json` (regenerate via scripts/build_showcase_metrics.py
// after meaningful product changes). All case studies pulled from REAL
// ProgressPair / RepositionAlert rows in state.db — see manifest.json
// for the s3_key → out-file mapping.
import showcaseMetrics from "./showcase_metrics.json";

export const liveMetrics = showcaseMetrics;

const p = liveMetrics.production;
const b = liveMetrics.benchmark;
if (!b.winner) {
  throw new Error(
    "showcase_metrics.json missing benchmark winner — regenerate via scripts/build_showcase_metrics.py",
  );
}
const winner = b.winner;

// ─── Hero ─────────────────────────────────────────────────────────────────

export const hero = {
  eyebrow: "Foton · moduł Analiza Postępu Remontów",
  badge: "AI dla inwestycji remontowych",
  headline: "Twoje kamery już patrzą. Niech AI to liczy.",
  sub:
    "Foton automatycznie zamienia zdjęcia z CCTV w gotowe propozycje aktualizacji " +
    "postępu prac. Koordynator dostaje zwięzłą kartę z fotkami before/after i opisem — " +
    "klika ✓ albo poprawia w sekundę. Bez przepisywania, bez wymyślania procentów, " +
    "bez ślęczenia nad galerią.",
  primaryCta: { label: "Zobacz cztery realne decyzje", href: "#scenarios" },
  secondaryCta: { label: "Co to robi w panelu", href: "#workflow" },
};

export const heroStats: { value: string; label: string; sub: string }[] = [
  {
    value: `$${p.llm_cost_per_call_usd?.toFixed(5) ?? "—"}`,
    label: "koszt decyzji AI",
    sub: "Skala produkcji kosztuje grosze.",
  },
  {
    value: `${Math.round(winner.stage_acc * 100)}%`,
    label: "trafność etapu",
    sub: `Na n=${winner.n_completed} ręcznie zweryfikowanych par.`,
  },
  {
    value: `${b.models_tested}`,
    label: "modeli wizyjnych w teście",
    sub: "Wybór modelu produkcyjnego ma uzasadnienie.",
  },
];

// ─── Problem ──────────────────────────────────────────────────────────────

export const problem = {
  eyebrow: "Dlaczego",
  titleStart: "Wiesz, co dziś",
  titleItalic: "zrobili na Twoim remoncie?",
  pitchTitle: "Codzienny obraz Twojego remontu.",
  pitchItalic: "Bez czekania na tygodniowy raport.",
  body: [
    "Żeby wiedzieć, co się dziś dzieje na Twojej budowie, koordynator dziś musi tam " +
      "pojechać — wizyta, rozmowa z kierownikiem, notatka, potem telefon do Ciebie. " +
      "Razy dwanaście inwestycji równolegle. W praktyce wychodzi update raz w tygodniu, " +
      "czasem z opóźnieniem.",
    "Foton odwraca ten układ. AI codziennie czyta klatki z kamer, w panelu koordynatora " +
      "pojawia się powiadomienie — co się zmieniło na której budowie. Wchodzi w panel, " +
      "w pół minuty zatwierdza, edytuje albo odrzuca propozycję postępu. Ty otwierasz " +
      "swój panel klienta — masz tam aktualne procenty postępu, galerię zdjęć z placu, " +
      "raporty, wiadomości od koordynatora. Bez telefonów, bez czekania na koniec tygodnia.",
  ],
};

// ─── Cadence ──────────────────────────────────────────────────────────────

export type CadenceDay = {
  d: string;
  events: { time: string; text: string }[];
  fillerLabel?: string;
};

export const cadence = {
  eyebrow: "Tryb pracy",
  titleStart: "Foton patrzy codziennie.",
  titleItalic: "Odzywa się tylko, kiedy coś się zmienia.",
  body:
    "Foton patrzy każdego dnia, ale dostajesz tylko to, co warto wiedzieć. Tydzień " +
    "zwykle przynosi parę konkretnych postępów — pozostałe dni brygada pracuje nad " +
    "tym, czego jeszcze nie widać, albo ma wolne. Nikt nie zawraca Ci głowy " +
    "„nic nowego”.",
  days: [
    { d: "Pn", events: [{ time: "08:13", text: "Malowanie kuchni — +15%" }] },
    { d: "Wt", events: [], fillerLabel: "brygada w pracy" },
    { d: "Śr", events: [{ time: "08:11", text: "Płytki łazienki — +12%" }] },
    { d: "Cz", events: [], fillerLabel: "brygada w pracy" },
    { d: "Pt", events: [{ time: "08:14", text: "Gładź salonu — +8%" }] },
    { d: "Sb", events: [], fillerLabel: "weekend" },
    { d: "Nd", events: [], fillerLabel: "weekend" },
  ] as CadenceDay[],
};

// ─── Scenarios (gallery of 4 real DB-backed cases) ────────────────────────

export type ScenarioKind = "approved" | "edited" | "skipped" | "reposition";

export type Scenario = {
  id: string;
  kind: ScenarioKind;
  badge: string;          // status badge in PL: "Zatwierdzone", "Skorygowane", ...
  outcomeLabel: string;   // "→ Do CRM", "✗ Nie wysyłamy", "🔒 Lock kamery"
  area: string;
  phase: string;
  cameraId: string;
  capturedBefore: string;
  capturedAfter: string;
  imgBefore: string;
  imgAfter: string;
  aiHeadline: string;     // first-person AI summary
  aiPickedDelta: string;  // e.g. "+8 pp" or "—"
  coordinatorVerdict: string;  // what the human did
  whyItMatters: string;   // 2-3 sentences explaining commercial value
};

export const scenarios: Scenario[] = [
  {
    id: "approved",
    kind: "approved",
    badge: "AI proponuje, koordynator klika ✓",
    outcomeLabel: "Decyzja → CRM",
    area: "Apartament Słoneczna 12/3 · Salon",
    phase: "Gładź ścian",
    cameraId: "CAM-0002",
    capturedBefore: "15.05.2026",
    capturedAfter: "19.05.2026",
    imgBefore: "/showcase/case-approved-before.jpg",
    imgAfter: "/showcase/case-approved-after.jpg",
    aiHeadline: "Postęp w gładzeniu ścian salonu — z 22% do 30%.",
    aiPickedDelta: "+8%",
    coordinatorVerdict: "Koordynator zatwierdził bez poprawek. Klient zobaczył nowy procent w panelu w tej samej chwili.",
    whyItMatters:
      "Standardowy dzień, najczęstszy scenariusz. AI rozpoznało obszar pracy " +
      "i postęp. Koordynator zerknął, kliknął Zatwierdź — i tyle. Klient nie " +
      "musiał o nic pytać.",
  },
  {
    id: "obstructed",
    kind: "skipped",
    badge: "Robotnicy zasłaniają kadr",
    outcomeLabel: "Wstrzymane przez AI",
    area: "Apartament Słoneczna 12/3 · Łazienka",
    phase: "—",
    cameraId: "CAM-0006",
    capturedBefore: "12.02.2026",
    capturedAfter: "13.02.2026 · 14:32",
    imgBefore: "/showcase/case-obstructed-before.jpg",
    imgAfter: "/showcase/case-obstructed-after.jpg",
    aiHeadline: "Łazienka zasłonięta — ekipa stoi dokładnie przed obszarem pracy.",
    aiPickedDelta: "—",
    coordinatorVerdict: "Foton sam wycofał propozycję. Czekamy na zdjęcie bez ludzi w kadrze.",
    whyItMatters:
      "Gdy nie widać tego, co Foton ma ocenić, AI nie zgaduje — wycofuje siebie samego. " +
      "Bez tego do panelu klienta trafiałyby liczby oparte na połowicznych obrazach — " +
      "szum, który nikomu nie pomaga.",
  },
  {
    id: "no-change",
    kind: "skipped",
    badge: "Brak realnej zmiany",
    outcomeLabel: "Wstrzymane przed LLM",
    area: "Apartament Słoneczna 12/3 · Salon (CAM-0005)",
    phase: "—",
    cameraId: "CAM-0005",
    capturedBefore: "12.05.2026",
    capturedAfter: "14.05.2026",
    imgBefore: "/showcase/case-skip-before.jpg",
    imgAfter: "/showcase/case-skip-after.jpg",
    aiHeadline: "Bez zmian — ten sam widok, ten sam układ rzeczy.",
    aiPickedDelta: "—",
    coordinatorVerdict: "Foton sam ocenił, że nie ma o czym pisać. Koordynator nie zobaczył nic w panelu.",
    whyItMatters:
      "Tak działa większość dni. Brygada pracuje, ale efekt jeszcze nie widoczny — " +
      "albo weekend, albo przygotowania. Foton nie zaśmieca panelu pustymi raportami: " +
      "cisza, dopóki naprawdę coś się nie zmieni.",
  },
  {
    id: "reposition",
    kind: "reposition",
    badge: "Alert: scena drastycznie inna",
    outcomeLabel: "Kamera zalockowana · operator decyduje",
    area: "Apartament Słoneczna 12/3 · Łazienka (CAM-0006)",
    phase: "—",
    cameraId: "CAM-0006",
    capturedBefore: "02.03.2026",
    capturedAfter: "09.03.2026",
    imgBefore: "/showcase/case-reposition-last.jpg",
    imgAfter: "/showcase/case-reposition-new.jpg",
    aiHeadline: "Widok zupełnie inny niż wczoraj — coś się dzieje większego niż zwykły postęp.",
    aiPickedDelta: "—",
    coordinatorVerdict: "Operator sprawdził — to wyburzanie ściany, nie awaria kamery. Idziemy dalej, od nowego stanu.",
    whyItMatters:
      "Gdy między dwoma kadrami widok zmienia się drastycznie, Foton wstrzymuje kamerę " +
      "zamiast pisać do panelu nieprawdziwą liczbę. Operator weryfikuje raz — czy kamera " +
      "się przesunęła, czy w budowie coś się zmieniło — i Foton zaczyna porównywać " +
      "od nowego stanu.",
  },
];

// ─── Coordinator workflow (the review tile mockup) ────────────────────────

export type ReviewCard = {
  id: string;
  status: "pending" | "approved" | "modified" | "discarded";
  area: string;
  phase: string;
  cameraId: string;
  capturedAt: string;
  headline: string;
  deltaPct: number;
  baselinePct: number;
  proposedPct: number;
  imgBefore: string;
  imgAfter: string;
  reasoning: string;
};

// Real pair 47c03fbc, status=approved. CAM-0002 Salon, gładź ścian.
// Clean photos in both frames (no workers, no reposition), real progress
// visible — coordinator approved without edits, went to CRM.
export const heroCard: ReviewCard = {
  id: "approved-pending",
  status: "pending",
  area: "Apartament Słoneczna 12/3 · Salon",
  phase: "Gładź ścian",
  cameraId: "CAM-0002",
  capturedAt: "19.05.2026 · 08:48",
  headline: "Postęp w gładzeniu ścian salonu.",
  deltaPct: 8,
  baselinePct: 22,
  proposedPct: 30,
  imgBefore: "/showcase/case-approved-before.jpg",
  imgAfter: "/showcase/case-approved-after.jpg",
  reasoning:
    "Ściany w salonie są w trakcie gładzenia. Widoczny jest postęp w porównaniu " +
    "do poprzedniego zdjęcia: powierzchnie między oknem a marmurową ścianą " +
    "zostały wygładzone, rusztowanie przesunięto.",
};

// ─── Flow ─────────────────────────────────────────────────────────────────

export type FlowStage = {
  n: string;
  name: string;
  sub: string;
  icon: string;
  primary?: boolean;
};

export const flow = {
  eyebrow: "Jak to działa",
  titleStart: "Cztery kroki, około czternastu minut.",
  titleItalic: "Każdego ranka.",
  footer: "kamery → AI → panel koordynatora → panel klienta · w czasie rzeczywistym",
  stages: [
    { n: "01", name: "Kamery na placu", sub: "klatki na S3", icon: "◇" },
    { n: "02", name: "AI Fotonu", sub: "dobiera pary, ocenia postęp", icon: "✦", primary: true },
    { n: "03", name: "Panel koordynatora", sub: "propozycja na ekranie", icon: "◐" },
    { n: "04", name: "Twój panel klienta", sub: "procent · galeria · wiadomości", icon: "○" },
  ] as FlowStage[],
};

// ─── Coordinator view ─────────────────────────────────────────────────────

export type CoordModule = { name: string; sub: string; counter: string };

export type AiExample = {
  headline: string;
  meta: string;
  delta: string;
  range: string;
  before: string;
  after: string;
};

export const coordinatorView = {
  eyebrow: "Co to robi w panelu",
  titleStart: "Tu się dzieje cała robota.",
  titleItalic: "W panelu przeglądarki, w pół godziny rano.",
  footer: "każdy dzień · nowa decyzja · nowy postęp",
  modules: [
    { name: "KSeF", sub: "Faktury e-invoicing", counter: "3 do akcept." },
    { name: "Materiały", sub: "Pending · Ordered · Delivered", counter: "8 do zamów." },
    { name: "Pracownicy", sub: "Godziny · Frekwencja", counter: "47h tydz." },
    { name: "Kamery", sub: "CCTV · live status", counter: "6/6 online" },
    { name: "Wiadomości", sub: "Klient · Wykonawca", counter: "9 nowych" },
    { name: "Obserwacje", sub: "Z placu · Foreman", counter: "18 w tygodniu" },
  ] as CoordModule[],
  examples: [
    {
      headline: "Płytki w łazience · nowy układ.",
      meta: "CAM-0004 · 08:51 · do zatwierdzenia",
      delta: "+12 pp",
      range: "30% → 42%",
      before: "/showcase/case-worker-before.jpg",
      after: "/showcase/case-worker-after.jpg",
    },
    {
      headline: "Posadzka pod łazienkę · wylewka.",
      meta: "CAM-0007 · 08:54 · do zatwierdzenia",
      delta: "+5 pp",
      range: "55% → 60%",
      before: "/showcase/case-skip-before.jpg",
      after: "/showcase/case-skip-after.jpg",
    },
  ] as AiExample[],
};

// ─── Worker uploads (the second intake pipeline) ──────────────────────────
// CCTV is one half of the picture; the other half is what crews upload from
// their phones. AI verifies the claim, auto-tags area/phase, and filters
// junk/safety/unclear before any of it reaches the coordinator.

export type WorkerFilter = {
  tag: string;          // PL signal label
  line: string;         // what it means / what happens
  emphasis?: boolean;   // safety → filled treatment, surfaces first
};

export const workerUploads = {
  eyebrow: "Druga połowa obrazu",
  titleStart: "Wykonawcy i tak wysyłają zdjęcia z telefonu.",
  titleItalic: "Niech AI je zweryfikuje.",
  body:
    "Kamera pokazuje, co widać z jednego punktu. Reszta budowy żyje w zdjęciach, " +
    "które ekipa wrzuca z telefonu — „położyłem płytki”, „skończyłem gładź”. Foton " +
    "czyta każde z nich: sprawdza, czy zdjęcie potwierdza to, co wpisał wykonawca, " +
    "automatycznie rozpoznaje pomieszczenie i fazę, a śmieci i zagrożenia wyłapuje, " +
    "zanim trafią do koordynatora.",
  claim: {
    worker: "Wykonawca · ekipa wykończeniowa",
    claimText: "„Skończyłem układanie płytek w łazience na parterze.”",
    img: "/showcase/case-worker-after.jpg",
    imgAlt: "Łazienka na parterze — płytki ułożone na ścianie, fugowanie w toku",
    matchLabel: "Zgodne z deklaracją",
    area: "Łazienka parter",
    phase: "Układanie płytek",
    aiVerdict:
      "Zdjęcie potwierdza deklarację — płytki ułożone na całej ścianie, fugowanie " +
      "w toku. Auto-tag: Łazienka · układanie płytek. Jakość 8/10.",
  },
  filtersLabel: "· co Foton odsiewa, zanim zobaczy to koordynator ·",
  filters: [
    {
      tag: "Zagrożenie",
      line:
        "Brak hełmu, odkryte kable, niezabezpieczone rusztowanie — ląduje na samej " +
        "górze kolejki koordynatora, osobną ścieżką eskalacji.",
      emphasis: true,
    },
    {
      tag: "Śmieć",
      line:
        "Selfie, pusty kadr, zrzut ekranu, zdjęcie nie z budowy — odrzucone, nigdy " +
        "nie zaśmieca panelu i nie kosztuje wywołania AI dwa razy.",
    },
    {
      tag: "Za mało widać",
      line:
        "Zdjęcie zbyt niejasne, żeby ocenić — Foton prosi wykonawcę o lepszy kadr, " +
        "zamiast zgadywać i wpisywać liczbę z połowicznego obrazu.",
    },
    {
      tag: "Niezgodne z opisem",
      line:
        "Zdjęcie nie pokazuje tego, co wpisał wykonawca — trafia do koordynatora " +
        "oznaczone jako sprzeczność, nie jako gotowy postęp.",
    },
  ] as WorkerFilter[],
  closing:
    "Z czasem każdy wykonawca dorabia się reputacji — kto wysyła rzetelne zdjęcia, " +
    "a kto podbija statystyki. Koordynator widzi kolejkę poukładaną według pilności, " +
    "nie według kolejności wrzucania.",
};

// ─── Foton CTA ────────────────────────────────────────────────────────────

export type CtaModule = { name: string; sub: string; primary?: boolean };

export const cta = {
  eyebrow: "Foton",
  titleStart: "Analiza postępu to jeden moduł.",
  titleItalic: "Foton ma osiem.",
  primaryCta: { label: "Wejdź na foton.works", href: "https://foton.works" },
  modules: [
    { name: "Analiza postępu", sub: "AI · widzieliście wyżej", primary: true },
    { name: "Panel koordynatora", sub: "KPI · powiadomienia · decyzje" },
    { name: "Panel klienta", sub: "galeria · raporty · wiadomości" },
    { name: "KSeF", sub: "faktury e-invoicing" },
    { name: "Materiały", sub: "zakupy · dostawy" },
    { name: "Pracownicy", sub: "godziny · frekwencja" },
    { name: "Kamery", sub: "CCTV · status live" },
    { name: "Wiadomości", sub: "klient · wykonawca · wątki" },
  ] as CtaModule[],
};
